import { useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { KeyReturn, User as UserIcon, Palette, CheckCircle } from "@phosphor-icons/react";

export default function ProfilePage() {
    const { user } = useAuth();
    const { themes, activeCode, setTheme, orgDefault, setOrgDefaultTheme } = useTheme();
    const isAdmin = user?.role === "org_admin" || user?.role === "platform_admin";
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [saving, setSaving] = useState(false);
    const [savingOrg, setSavingOrg] = useState(false);

    const pickTheme = async (code) => {
        try { await setTheme(code); toast.success("Theme updated"); }
        catch (e) { toast.error(extractError(e)); }
    };

    const setAsOrgDefault = async (code) => {
        if (!isAdmin) return;
        setSavingOrg(true);
        try {
            await setOrgDefaultTheme(code);
            toast.success(code ? "Org default theme set" : "Org default cleared");
        } catch (e) { toast.error(extractError(e)); }
        finally { setSavingOrg(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.new_password !== form.confirm) {
            toast.error("New password and confirmation do not match");
            return;
        }
        if (form.new_password.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Password updated successfully");
            setForm({ current_password: "", new_password: "", confirm: "" });
        } catch (err) { toast.error(extractError(err)); }
        finally { setSaving(false); }
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Account"
                title="Your profile"
                description="Manage your personal account settings."
                testid="profile-header"
            />

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
                <div className="brut-section p-8" data-testid="profile-info-section">
                    <div className="flex items-center gap-2 mb-6">
                        <UserIcon size={20} weight="duotone" />
                        <h2 className="text-xl font-bold tracking-tight">Profile</h2>
                    </div>
                    <dl className="space-y-3 text-sm">
                        <div className="flex justify-between border-b border-border pb-3"><dt className="label-overline">Name</dt><dd>{user?.name}</dd></div>
                        <div className="flex justify-between border-b border-border pb-3"><dt className="label-overline">Email</dt><dd className="font-mono text-xs">{user?.email}</dd></div>
                        <div className="flex justify-between border-b border-border pb-3"><dt className="label-overline">Role</dt><dd className="font-mono text-xs capitalize">{user?.role?.replace("_", " ")}</dd></div>
                        <div className="flex justify-between"><dt className="label-overline">Organization</dt><dd>{user?.org_name || "—"}</dd></div>
                    </dl>
                </div>

                <div className="brut-section p-8" data-testid="change-password-section">
                    <div className="flex items-center gap-2 mb-6">
                        <KeyReturn size={20} weight="duotone" />
                        <h2 className="text-xl font-bold tracking-tight">Change password</h2>
                    </div>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label className="label-overline">Current password</Label>
                            <Input
                                type="password" required value={form.current_password}
                                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                                className="mt-2 rounded-sm h-11" data-testid="current-password-input"
                            />
                        </div>
                        <div>
                            <Label className="label-overline">New password</Label>
                            <Input
                                type="password" required minLength={6} value={form.new_password}
                                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                                placeholder="6+ characters" className="mt-2 rounded-sm h-11" data-testid="new-password-input"
                            />
                        </div>
                        <div>
                            <Label className="label-overline">Confirm new password</Label>
                            <Input
                                type="password" required minLength={6} value={form.confirm}
                                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                className="mt-2 rounded-sm h-11" data-testid="confirm-password-input"
                            />
                        </div>
                        <Button
                            type="submit" disabled={saving}
                            className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                            data-testid="change-password-submit-btn"
                        >
                            {saving ? "Updating…" : "Update password"}
                        </Button>
                    </form>
                </div>
            </div>

            <div className="brut-section p-8" data-testid="themes-section">
                <div className="flex items-center gap-2 mb-2">
                    <Palette size={20} weight="duotone" />
                    <h2 className="text-xl font-bold tracking-tight">Appearance</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-2">Pick a workspace theme. Your choice syncs to all devices.</p>
                {isAdmin && (
                    <div className="mb-6 border border-dashed border-border rounded-sm bg-secondary/30 px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid="org-default-theme-bar">
                        <span className="label-overline">Org default</span>
                        <span className="font-mono">{orgDefault || "— (none set)"}</span>
                        {orgDefault && <span className="text-muted-foreground">applies to every user who hasn't picked their own.</span>}
                        {orgDefault && (
                            <Button variant="outline" size="sm" className="rounded-sm h-7 ml-auto" disabled={savingOrg} onClick={() => setAsOrgDefault(null)} data-testid="org-default-clear">Clear</Button>
                        )}
                    </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="theme-grid">
                    {themes.length === 0 && <p className="text-sm text-muted-foreground">No themes available yet — the platform admin can add some.</p>}
                    {themes.map((t) => {
                        const isActive = (activeCode || "classic-navy") === t.code;
                        const primary = `hsl(${t.tokens?.primary || "224 100% 33%"})`;
                        const bg = `hsl(${t.tokens?.background || "0 0% 100%"})`;
                        const fg = `hsl(${t.tokens?.foreground || "222 47% 11%"})`;
                        return (
                            <button
                                key={t.id} type="button"
                                onClick={() => pickTheme(t.code)}
                                className={`text-left p-0 rounded-sm border-2 transition-all overflow-hidden ${isActive ? "border-primary shadow-md" : "border-border hover:border-foreground/30"}`}
                                data-testid={`theme-${t.code}`}
                            >
                                <div className="h-20 relative" style={{ background: bg, color: fg }}>
                                    <div className="absolute inset-3 flex flex-col gap-1.5">
                                        <div className="h-2 w-3/5 rounded-sm" style={{ background: primary }} />
                                        <div className="h-1.5 w-2/3 rounded-sm opacity-30" style={{ background: fg }} />
                                        <div className="h-1.5 w-1/2 rounded-sm opacity-30" style={{ background: fg }} />
                                    </div>
                                    {isActive && (
                                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                                            <CheckCircle size={12} weight="fill" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-3 bg-card">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-semibold">{t.name}</span>
                                        {t.is_seeded && <Badge variant="outline" className="rounded-sm font-mono text-[9px]">SEEDED</Badge>}
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{t.description}</p>
                                    {isAdmin && (
                                        <button type="button" onClick={(e) => { e.stopPropagation(); setAsOrgDefault(t.code); }} disabled={savingOrg} className={`mt-2 w-full text-[10px] py-1 px-2 rounded-sm font-mono uppercase tracking-wider transition-colors ${orgDefault === t.code ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:border-foreground hover:text-foreground"}`} data-testid={`org-default-set-${t.code}`}>
                                            {orgDefault === t.code ? "✓ Org default" : "Set as org default"}
                                        </button>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </PageContainer>
    );
}
